import type { MutationKey, QueryClient, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Result } from 'true-myth';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';

import { unwrapResult } from '../../fetchers/unwrap-result';

type InvalidatingMutationOptions<Variables> = {
    readonly mutationKey?: MutationKey;
    readonly onError?: (queryClient: QueryClient, error: Error, variables: Variables) => void;
};

export const useInvalidatingMutation = <Variables, Data>(
    mutate: (variables: Variables) => Promise<Result<Data, HttpFetcherError>>,
    invalidate: (queryClient: QueryClient, variables: Variables, data: Data) => Promise<void> | void,
    { mutationKey, onError }: InvalidatingMutationOptions<Variables> = {},
): UseMutationResult<Data, Error, Variables> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables) => unwrapResult(mutate(variables)),
        mutationKey,
        onError: (error, variables) => onError?.(queryClient, error, variables),
        onSuccess: (data, variables) => invalidate(queryClient, variables, data),
    });
};
